import { useState, useEffect } from "react";
import { Modal } from "@/components/ui/modal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { DateRangePicker } from "@/components/ui/date-range-picker";
import { UploadMultiImages } from "@/components/ui/upload-multi-images";
import { UploadMultiFiles } from "@/components/ui/upload-multi-files";
import { Link, MapPin, Euro, ArrowRight } from "lucide-react";
import { AccommodationActivity } from "../models/activity.model";

interface AccommodationModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (activity: Partial<AccommodationActivity>) => void;
    icon?: React.ReactNode;
    initialData?: Partial<AccommodationActivity>;
    // optional itinerary bounds (may be provided by caller)
    itineraryStartDate?: string | null;
    itineraryEndDate?: string | null;
    itineraryId: string;
}

const AccommodationModal = ({ isOpen, onClose, onSave, icon, initialData, itineraryStartDate, itineraryEndDate, itineraryId }: AccommodationModalProps) => {
    // Generate temporary UUID for new activities
    const [activityId] = useState<string>(() => 
        initialData?.id || crypto.randomUUID()
    );

    const [formData, setFormData] = useState<Partial<AccommodationActivity>>({
        id: activityId,
        include_price: true,
        title: '',
        link: '',
        address: '',
        start_date: undefined,
        end_date: undefined,
        check_in_time: '',
        check_out_time: '',
        check_in_note: '',
        cost: 0,
        refundable_rate: false,
        price_tag: '',
        description: '',
        media: [],
        documentation: []
    });

    // Update form data when initialData changes
    useEffect(() => {
        if (initialData) {
            setFormData({ ...initialData, id: activityId });
        }
    }, [initialData, activityId]);

    const [errors, setErrors] = useState<Record<string, boolean>>({});

    const priceTagOptions = [
        'Only accommodation', 'Breakfast Included', 'Half board', 'Full board', 'All inclusive'
    ];

    const validateForm = () => {
        const requiredFields = ['title', 'link', 'address', 'start_date', 'end_date', 'check_in_time', 'check_out_time', 'cost', 'price_tag', 'description', 'media'];
        const newErrors: Record<string, boolean> = {};

        requiredFields.forEach(field => {
            const value = formData[field as keyof AccommodationActivity];
            if (field === 'cost') {
                newErrors[field] = value === null || value === undefined || (value as number) < 0;
            } else {
                newErrors[field] = !value || (typeof value === 'string' && value.trim() === '');
            }
        });

        setErrors(newErrors);
        return !Object.values(newErrors).some(error => error);
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (validateForm()) {
            onSave({
                ...formData,
                type: 'accommodation' as const
            });
            onClose();
        }
    };

    // derive min/max for date picker: prefer explicit props, then common fields on initialData
    const derivedMinDate: string | null =
        itineraryStartDate
        || (initialData as any)?.itineraryStartDate
        || (initialData as any)?.itinerary_start_date
        || (initialData as any)?.min_date
        || (initialData as any)?.minDate
        || null;

    const derivedMaxDate: string | null =
        itineraryEndDate
        || (initialData as any)?.itineraryEndDate
        || (initialData as any)?.itinerary_end_date
        || (initialData as any)?.max_date
        || (initialData as any)?.maxDate
        || null;

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={
                <div className="flex items-center gap-2">
                    {icon} Accommodation
                </div>
            }
            size="lg"
            footer={
                <div className="flex w-full justify-between">
                    <Button type="button" size="sm" variant="ghost" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button type="submit" size="sm" form="accommodation-form">
                        Confirm
                    </Button>
                </div>
            }
        >
            <form id="accommodation-form" onSubmit={handleSubmit} className="space-y-4 p-6">
                {/* Include Price Switch */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border">
                    <div>
                        <label className="text-sm font-medium text-gray-700">Include Price</label>
                        <p className="text-xs text-gray-500">Included in the total price of the package</p>
                    </div>
                    <Switch
                        checked={formData.include_price || false}
                        onCheckedChange={(checked) => setFormData(prev => ({ ...prev, include_price: checked }))}
                    />
                </div>

                {/* Basic Information */}
                <Card>
                    <CardHeader>
                        <CardTitle>General Information</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Input
                                    id="title"
                                    value={formData.title || ''}
                                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                                    placeholder="Accommodation Title"
                                    hasError={errors.title}
                                />
                            </div>
                            <div>
                                <div className="relative">
                                    <Link className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-900" />
                                    <Input
                                        id="link"
                                        type="url"
                                        value={formData.link || ''}
                                        onChange={(e) => setFormData(prev => ({ ...prev, link: e.target.value }))}
                                        placeholder="Accommodation Link (only visible to you)"
                                        className="pl-10"
                                        hasError={errors.link}
                                    />
                                </div>
                            </div>
                        </div>

                        <div>
                            <div className="relative">
                                <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-900" />
                                <Input
                                    id="address"
                                    value={formData.address || ''}
                                    onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                                    placeholder="Address"
                                    className="pl-10"
                                    hasError={errors.address}
                                />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Date Range and Check-in/Check-out Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Date Range Card */}
                    <Card>
                        <CardHeader>
                            <CardTitle>From - to</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <DateRangePicker
                                startDate={formData.start_date || ''}
                                endDate={formData.end_date || ''}
                                onStartDateChange={(date) => setFormData(prev => ({ ...prev, start_date: date }))}
                                onEndDateChange={(date) => setFormData(prev => ({ ...prev, end_date: date }))}
                                startDatePlaceholder="From"
                                endDatePlaceholder="To"
                                hasError={errors.start_date || errors.end_date}
                                // pass derived bounds so the date picker can limit selection
                                minDate={derivedMinDate || undefined}
                                maxDate={derivedMaxDate || undefined}
                            />
                        </CardContent>
                    </Card>

                    {/* Check-in & Check-out Card */}
                    <Card>
                        <CardHeader>
                            <CardTitle>Check-in & Check-out</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="flex flex-row justify-evenly items-center">
                                <div className="flex flex-row items-center">
                                    <Input
                                        className="w-30"
                                        type="time"
                                        value={formData.check_in_time || ''}
                                        onChange={(e) => setFormData(prev => ({ ...prev, check_in_time: e.target.value }))}
                                        placeholder="Check-in"
                                        hasError={errors.check_in_time}
                                    />
                                </div>
                                <span>
                                    <ArrowRight className="mx-2 h-4 w-4 text-gray-500" />
                                </span>
                                <div className="flex flex-row items-center">
                                    <Input
                                        className="w-30"
                                        type="time"
                                        value={formData.check_out_time || ''}
                                        onChange={(e) => setFormData(prev => ({ ...prev, check_out_time: e.target.value }))}
                                        placeholder="Check-out"
                                        hasError={errors.check_out_time}
                                    />
                                </div>
                            </div>

                            <div>
                                <Textarea
                                    id="checkInNote"
                                    value={formData.check_in_note || ''}
                                    onChange={(e) => setFormData(prev => ({ ...prev, check_in_note: e.target.value }))}
                                    placeholder="Share any additional check-in or check-out details"
                                    rows={3}
                                />
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Price Card */}
                <Card>
                    <CardHeader>
                        <CardTitle>Price</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {/* Price Input with Refundable Rate */}
                        <div className="flex items-center justify-center gap-4">
                            <div className="flex-1">
                                <div className="relative">
                                    <Euro className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-900" />
                                    <Input
                                        id="cost"
                                        type="number"
                                        min="0"
                                        step="1"
                                        value={formData.cost}
                                        onChange={(e) => setFormData(prev => ({ ...prev, cost: parseFloat(e.target.value) }))}
                                        placeholder="Amount (only visible to you)"
                                        className="pl-10"
                                        hasError={errors.cost}
                                    />
                                </div>
                            </div>
                            <div className="flex items-center space-x-2">
                                <Checkbox
                                    id="refundableRate"
                                    checked={formData.refundable_rate || false}
                                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, refundable_rate: !!checked }))}
                                />
                                <Label htmlFor="refundableRate" className="text-sm">Refundable rate</Label>
                            </div>
                        </div>

                        {/* Price Tags */}
                        <div className="mt-4">
                            <ToggleGroup
                                type="single"
                                value={formData.price_tag || ''}
                                onValueChange={(value) => setFormData(prev => ({ ...prev, price_tag: (value as string) || '' }))}
                                variant="outline"
                                className="flex items-start justify-start flex-wrap gap-0 mt-2"
                                hasError={errors.price_tag}
                            >
                                {priceTagOptions.map((tag, index) => (
                                    <ToggleGroupItem
                                        key={tag}
                                        value={tag}
                                        className={[
                                            "text-sm rounded-none first:ml-0",
                                            index === 0 ? "rounded-l-lg" : "",
                                            index === priceTagOptions.length - 1 ? "rounded-r-lg" : "",
                                        ].join(" ")}
                                    >
                                        {tag}
                                    </ToggleGroupItem>
                                ))}
                            </ToggleGroup>
                        </div>

                    </CardContent>
                </Card>

                {/* Description Card */}
                <Card>
                    <CardHeader>
                        <CardTitle>Description</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <Textarea
                            id="description"
                            value={formData.description || ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                            placeholder="Copy and paste the description of the accommodation, detailing the room type, the services offered and outlining the package included for your guests."
                            rows={6}
                            hasError={errors.description}
                        />
                    </CardContent>
                </Card>

                {/* Media and Documentation Cards */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {/* Media Card */}
                    <Card>
                        <CardHeader>
                            <CardTitle>Media</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <UploadMultiImages
                                images={formData.media || []}
                                onImagesChange={(images) => setFormData(prev => ({ ...prev, media: images }))}
                                label="Upload 4 photos (optional)"
                                maxImages={10}
                                hasError={errors.media}
                                itineraryId={itineraryId}
                                activityId={activityId}
                            />
                        </CardContent>
                    </Card>

                    {/* Documentation Card */}
                    <Card>
                        <CardHeader>
                            <CardTitle>Documentation</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <UploadMultiFiles
                                files={formData.documentation || []}
                                onFilesChange={(files) => setFormData(prev => ({ ...prev, documentation: files }))}
                                label="Upload your booking file or supporting documentation"
                                maxFiles={5}
                                itineraryId={itineraryId}
                                activityId={activityId}
                            />
                        </CardContent>
                    </Card>
                </div>

            </form>
        </Modal>
    );
};

export default AccommodationModal;
